import React, { useMemo } from 'react';
import styled from 'styled-components';
import CapabilityTagsField from './CapabilityTagsField';
import { getEditablePinAlternateNameEntries } from '../utils/pinAlternateNames';

const PIN_ROLE_OPTIONS = [
  { value: '', label: 'Select role...' },
  { value: 'power', label: 'Power' },
  { value: 'ground', label: 'Ground' },
  { value: 'input', label: 'Input' },
  { value: 'output', label: 'Output' },
  { value: 'io', label: 'I/O' },
  { value: 'analog', label: 'Analog' },
  { value: 'clock', label: 'Clock' },
  { value: 'reset', label: 'Reset' },
  { value: 'control', label: 'Control' },
  { value: 'communication', label: 'Communication' },
  { value: 'nc', label: 'No Connect' }
];

const SUGGESTED_PIN_CAPABILITY_TAGS = [
  'gpio',
  'digital',
  'pio',
  'digital-input',
  'digital-output',
  'analog-input',
  'pwm',
  'interrupt',
  'i2c-sda',
  'i2c-scl',
  'spi-mosi',
  'spi-miso',
  'spi-sck',
  'spi-cs',
  'uart-tx',
  'uart-rx',
  'power',
  'ground',
  'control',
  'reset',
  'clock',
  'enable',
  'active-low',
  'active-high',
  'open-drain',
  'tri-state'
];

const Container = styled.div`
  display: grid;
  gap: 18px;
`;

const Title = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 700;
`;

const HelperText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: grid;
  gap: 8px;
  grid-column: ${props => props.$full ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: ${props => props.disabled ? '#f8fafc' : 'white'};

  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: ${props => props.disabled ? '#f8fafc' : 'white'};

  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  background: ${props => props.disabled ? '#f8fafc' : 'white'};

  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const CheckboxChip = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #dbe4f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 13px;
  color: #334155;
  cursor: pointer;

  input {
    margin: 0;
  }
`;

const SmallHelperText = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  padding: 28px;
  text-align: center;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  color: #64748b;
  background: #f8fafc;
`;

const AlternateNameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AlternateNameCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AlternateNamePositionDetails = styled.details`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 10px 12px;
`;

const AlternateNamePositionSummary = styled.summary`
  cursor: pointer;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }
`;

const AlternateNamePositionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const getDefaultPinKey = (pin) => String(pin?.pin_number ?? pin?.pinNumber ?? pin?.id ?? '');

const PinMetadataEditor = ({
  pins = [],
  selectedPinKey = '',
  onSelectedPinKeyChange,
  onPinFieldChange,
  onPinAlternateNameChange,
  getPinKey = getDefaultPinKey,
  selectedPin = null,
  disabled = false
}) => {
  const activePin = useMemo(() => {
    if (selectedPin) {
      return selectedPin;
    }

    return pins.find((pin) => getPinKey(pin) === selectedPinKey) || null;
  }, [getPinKey, pins, selectedPin, selectedPinKey]);

  const activePinKey = activePin ? getPinKey(activePin) : selectedPinKey;
  const alternateNameEntries = getEditablePinAlternateNameEntries(activePin);

  const handleFieldChange = (field, value) => {
    if (!activePinKey || typeof onPinFieldChange !== 'function') {
      return;
    }

    onPinFieldChange(activePinKey, field, value);
  };

  const handleAlternateNameChange = (index, field, value) => {
    if (!activePinKey || typeof onPinAlternateNameChange !== 'function') {
      return;
    }

    onPinAlternateNameChange(activePinKey, index, field, value);
  };

  if (!Array.isArray(pins) || pins.length === 0) {
    return (
      <Container>
        <Title>Pin Metadata</Title>
        <EmptyState>No pins available yet.</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <div>
        <Title>Pin Metadata</Title>
        <HelperText>
          Select a pin and assign the same metadata style used by board pins.
        </HelperText>
      </div>

      <FormGroup $full>
        <Label>Select Pin</Label>
        <Select
          value={activePinKey}
          onChange={(e) => onSelectedPinKeyChange?.(e.target.value)}
          disabled={disabled}
        >
          {pins.map((pin) => {
            const pinKey = getPinKey(pin);
            const pinNumber = pin.pin_number ?? pin.pinNumber ?? '';
            const pinName = pin.pin_name || pin.pinName || `PIN${pinNumber}`;

            return (
              <option key={pinKey} value={pinKey}>
                Pin {pinNumber} - {pinName}
              </option>
            );
          })}
        </Select>
      </FormGroup>

      {activePin ? (
        <FormGrid>
          <FormGroup>
            <Label>Pin Name</Label>
            <Input
              type="text"
              value={activePin.pin_name || ''}
              onChange={(e) => handleFieldChange('pin_name', e.target.value)}
              disabled={disabled}
              placeholder="e.g. VCC, GND, QA, CLK"
            />
          </FormGroup>

          <FormGroup>
            <Label>Pin Role</Label>
            <Select
              value={activePin.pin_role || ''}
              onChange={(e) => handleFieldChange('pin_role', e.target.value)}
              disabled={disabled}
            >
              {PIN_ROLE_OPTIONS.map((option) => (
                <option key={option.value || 'empty'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Primary Function</Label>
            <Input
              type="text"
              value={activePin.functions || ''}
              onChange={(e) => handleFieldChange('functions', e.target.value)}
              disabled={disabled}
              placeholder="e.g. Serial data input"
            />
          </FormGroup>

          <FormGroup>
            <Label>Current Limit (mA)</Label>
            <Input
              type="number"
              min="0"
              step="0.001"
              value={activePin.current_limit_ma ?? ''}
              onChange={(e) => handleFieldChange('current_limit_ma', e.target.value)}
              disabled={disabled}
              placeholder="e.g. 20"
            />
          </FormGroup>

          <FormGroup>
            <Label>Voltage Min (V)</Label>
            <Input
              type="number"
              min="0"
              step="0.001"
              value={activePin.voltage_min_v ?? ''}
              onChange={(e) => handleFieldChange('voltage_min_v', e.target.value)}
              disabled={disabled}
              placeholder="e.g. 0"
            />
          </FormGroup>

          <FormGroup>
            <Label>Voltage Max (V)</Label>
            <Input
              type="number"
              min="0"
              step="0.001"
              value={activePin.voltage_max_v ?? ''}
              onChange={(e) => handleFieldChange('voltage_max_v', e.target.value)}
              disabled={disabled}
              placeholder="e.g. 5"
            />
          </FormGroup>

          <FormGroup>
            <Label>5V Tolerant</Label>
            <CheckboxChip>
              <input
                type="checkbox"
                checked={Boolean(activePin.is_5v_tolerant)}
                onChange={(e) => handleFieldChange('is_5v_tolerant', e.target.checked)}
                disabled={disabled}
              />
              <span>Safe for 5V logic on this pin</span>
            </CheckboxChip>
          </FormGroup>

          <FormGroup $full>
            <Label>Capability Tags</Label>
            <CapabilityTagsField
              value={activePin.capability_tags}
              onChange={(nextTags) => handleFieldChange('capability_tags', nextTags)}
              suggestedTags={SUGGESTED_PIN_CAPABILITY_TAGS}
              disabled={disabled}
              placeholder="e.g. gpio, spi-mosi, active-low"
              inputAriaLabel="Capability tags"
              helperText="Type free-form tags, or click a suggested tag to insert it."
            />
          </FormGroup>

          <FormGroup $full>
            <Label>Alternate Names</Label>
            <AlternateNameGrid>
              {alternateNameEntries.map((alternateNameEntry, index) => (
                <AlternateNameCard key={`alternate-name-${index}`}>
                  <Input
                    type="text"
                    value={alternateNameEntry.name}
                    onChange={(e) => handleAlternateNameChange(index, 'name', e.target.value)}
                    disabled={disabled}
                    placeholder={`Alternate name ${index + 1}`}
                  />
                  <AlternateNamePositionDetails defaultOpen={Boolean(alternateNameEntry.x || alternateNameEntry.y)}>
                    <AlternateNamePositionSummary>
                      Optional position override
                    </AlternateNamePositionSummary>
                    <AlternateNamePositionGrid>
                      <Input
                        type="number"
                        step="0.1"
                        value={alternateNameEntry.x}
                        onChange={(e) => handleAlternateNameChange(index, 'x', e.target.value)}
                        disabled={disabled}
                        placeholder="X"
                      />
                      <Input
                        type="number"
                        step="0.1"
                        value={alternateNameEntry.y}
                        onChange={(e) => handleAlternateNameChange(index, 'y', e.target.value)}
                        disabled={disabled}
                        placeholder="Y"
                      />
                    </AlternateNamePositionGrid>
                  </AlternateNamePositionDetails>
                </AlternateNameCard>
              ))}
            </AlternateNameGrid>
            <SmallHelperText>
              Add up to four alternate labels for this pin. X and Y use SVG coordinates for the alternate callout box.
            </SmallHelperText>
          </FormGroup>

          <FormGroup $full>
            <Label>Alternate Functions</Label>
            <TextArea
              value={activePin.alternate_functions || ''}
              onChange={(e) => handleFieldChange('alternate_functions', e.target.value)}
              disabled={disabled}
              placeholder="e.g. SPI, I2C, clock input, output enable, reset"
            />
          </FormGroup>

          <FormGroup $full>
            <Label>Description</Label>
            <TextArea
              value={activePin.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={disabled}
              placeholder="Add pin-specific notes, warnings, and common usage details..."
            />
          </FormGroup>
        </FormGrid>
      ) : (
        <EmptyState>Select a pin to edit its metadata.</EmptyState>
      )}
    </Container>
  );
};

export default PinMetadataEditor;
